import React, { FunctionComponent, useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_one, fetch_all } from "../../lib/v31lib";
import { loginAtom } from '../../lib/auth';
import { currentPracticeAtom } from '../../lib/practice';

import BrunchDiningIcon from '@mui/icons-material/BrunchDining';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';

import {
  Alert,
  Avatar,
  Box,
  Button as Btn,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Step,
  StepLabel,
  Stepper
} from '@mui/material';
import {
  GaugeContainer,
  GaugeValueArc,
  GaugeValueText,
  GaugeReferenceArc,
  useGaugeState,
} from '@mui/x-charts/Gauge';


import {
  Button,
  Card,
  DatePicker,
  Dt,
  Page,
  Switcher,
  TextInput,
  Typography
} from '../../components/v2/styled';
import { MoveMoment } from '../../components/v2/balls/MoveMoment';
import { ClientSaveWeight } from '../../components/v2/dialogs/ClientSaveWeight';
import { ClientEatMomentEdit } from '../../components/v2/dialogs/ClientEatMomentEdit';
import { ClientMoveMomentEdit } from '../../components/v2/dialogs/ClientMoveMomentEdit';
import { EatDayOverview } from '../../components/v2/dialogs/EatDayOverview';
import { SnackSaved } from '../../components/v2/snacks/Saved';

import { DiaryDay } from '../../models/DiaryDay';
import { DiaryFeedback } from '../../models/DiaryFeedback';
import { EatMoment as EatMmnt } from '../../models/EatMoment';
import { MoveMoment as MoveMmnt } from '../../models/MoveMoment';
import Edit from '@mui/icons-material/Edit';
import { Consent } from '../../models/Consent';
import { AdhProfile } from '../../models/AdhProfile';
import { FEATURES } from '../../features';
import HelpersPractice from '../../actions/helpers/practice';

const { DateTime } = require("luxon");

type Props = {}

export const ClientDiary: FunctionComponent<Props> = ({}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);
  const queryClient = useQueryClient();
  const { state } = useLocation();

  const [currentDay, setCurrentDay] = useState<string>(!!state && !!state.day ? state.day : DateTime.now().toFormat("dd/LL/y"));
  const [currentNote, setCurrentNote] = useState<string>('');
  const [hidePractices, setHidePractices] = useState<string>('');
  const [currentNoteHidden, setCurrentNoteHidden] = useState<boolean>(false);
  const [currentNoteSaved, setCurrentNoteSaved] = useState<boolean>(false);
  const [activeAdh, setActiveAdh] = useState<number>();

  const {
    data: diaryDay,
    isLoading: diaryDayLoading,
    isError: diaryDayError,
    isSuccess: diaryDaySuccess,
    refetch: diaryDayRefetch,
  } = useQuery({
    queryKey: ["diary", "diary_day", login?.id, currentDay],
    queryFn: () =>
      fetch_all<DiaryDay>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'diary_day',
          fields: [
            'id', 'day', 'wake_time', 'sleep_time', 'move_moment_list', 'breakfast', 'lunch', 'diner', 'snack1', 'snack2', 'snack3', 'weight',
            'external_feedbacks', 'client_note', 'client_note_hidden', 'hide_practices', 'eat_calculation', 'latest_weight'
          ],
          sub_fields: {
            external_feedbacks: ['id', 'practice|id/name', 'user|id/full_name', 'comment']
          },
          per_page: 1,
          page: 0,
          filter: {
            advanced: {
              day: currentDay,
              user_id: login?.id
            }
          }
        },
        login
      ),
    select: (d) => {
      return d[0];
    },
    enabled: !!login && !!currentDay,
  });

  const {
    data: adhProfile,
    isLoading: adhProfileLoading,
    // isError: adhProfileError,
    // isSuccess: adhProfileSuccess,
    // refetch: adhProfileRefetch,
  } = useQuery({
    queryKey: ["adh_profile", activeAdh],
    queryFn: () =>
      fetch_one<AdhProfile>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'adh_profile',
          fields: [
            'id', 'name', 'adh_links'
          ],
          sub_fields: {
            adh_links: ['id', 'nutritional_value', 'nutritional_value_id', 'lower_limit', 'higher_limit', 'maximum', 'is_tracked_client', 'measure_mode'],
            nutritional_value: ['id', 'name', 'is_grams_per_kilogram', 'measure', 'is_client_enabled']
          },
          id: activeAdh
        },
        login
      ),
    enabled: !!login && !!activeAdh,
  });
  const {
    data: diaryFeedbacks,
    isLoading: diaryFeedbacksLoading,
    isError: diaryFeedbacksError,
    isSuccess: diaryFeedbacksSuccess,
    refetch: diaryFeedbacksRefetch,
  } = useQuery({
    queryKey: ["diary", "feedbacks", login?.id],
    queryFn: () =>
      fetch_all<DiaryFeedback>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'diary_feedback',
          fields: [
            'id', 'object', 'object_type', 'thumb', 'comment', 'user|id/full_name', 'created_at', 'object|day|day', 'object|eat_moment|day|day'
          ],
          order: "created_at DESC",
          per_page: 5,
          page: 0,
          filter: {
            advanced: {
              for_user: login?.id
            }
          }
        },
        login
      ),
    // select: (d) => {
    //   return d[0];
    // },
    enabled: !!login && !!currentDay,
  });
  const {
    data: myPractices,
    // isLoading: objectsLoading,
    // isError: objectsError,
    // isSuccess: objectsSuccess,
    // refetch: objectsRefetch,
  } = useQuery({
    queryKey: ["my_practices_for_dashboard_extended", login?.practice_id],
    queryFn: () =>
      fetch_all<Consent>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'consent',
          fields: ['id', 'practice', 'practice_id', 'enabled', 'is_daynote', 'adh_profile_id', 'updated_at'],
          sub_fields: {
            practice: ['id', 'name', 'get_medium', 'item_is_square', 'url_path', 'modules']
          },
          filter: {
            search: '',
            advanced: {
              user_id: login?.id
            }
          }
        },
        login
      ),
    enabled: !!login && login.type === "User::User"
  });

  const canDoCalculated = FEATURES.CALCULATED_DIARY && !!HelpersPractice.practice_modules_v3({practice: {modules: (myPractices || []).filter(fff => !!fff.enabled && !!fff.practice).map(fff => fff.practice).flatMap(ppp => (ppp as any).modules || [])}, check: 'calculated_diary'});







  const mutationSaveClientNote = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<{
        custom_result: {
          success: boolean,
          error?: string
        }
      }>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        pars.formData,
        login
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      return true;
    },
    onError: (data, variables, context) => {
      return true;
    },
    onSuccess: (data, variables, context) => {
      diaryDayRefetch();
      queryClient.invalidateQueries({ queryKey: ["diary_day", login?.id, currentDay] });
      setCurrentNoteSaved(true);
    },
  });
  const mutationSaveEatMoment = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<{
        custom_result: {
          success: boolean,
          error?: string
        }
      }>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        pars.formData,
        login
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      // setIsEditPasswordError(false);
      // setEditPasswordSuccess(false);
      // setEditPasswordError(undefined);
      // setEditPasswordLoading(true);
      return true;
    },
    onError: (data, variables, context) => {
      // setIsEditPasswordError(true);
      // setEditPasswordError(data as any);
      // setEditPasswordLoading(false);
      return true;
    },
    onSuccess: (data, variables, context) => {
      setEatOpen(false);
      setNewEatMoment({feeling: -1, hunger: -1, item: undefined});
      diaryDayRefetch();
      queryClient.invalidateQueries({ queryKey: ["diary_day", login?.id, currentDay] });
      // snack?
    },
  });
  const mutationSaveMoveMoment = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<{
        custom_result: {
          success: boolean,
          error?: string
        }
      }>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/save`,
        pars.formData,
        login
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      // setIsEditPasswordError(false);
      // setEditPasswordSuccess(false);
      // setEditPasswordError(undefined);
      // setEditPasswordLoading(true);
      return true;
    },
    onError: (data, variables, context) => {
      // setIsEditPasswordError(true);
      // setEditPasswordError(data as any);
      // setEditPasswordLoading(false);
      return true;
    },
    onSuccess: (data, variables, context) => {
      setMoveOpen(false);
      setNewMoveMoment({feeling: -1, intensity: -1});
      diaryDayRefetch();
      queryClient.invalidateQueries({ queryKey: ["diary_day", login?.id, currentDay] });
      // snack?
    },
  });
  const mutationSaveWeight = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<{
        custom_result: {
          success: boolean,
          error?: string
        }
      }>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        pars.formData,
        login
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      // setIsEditPasswordError(false);
      // setEditPasswordSuccess(false);
      // setEditPasswordError(undefined);
      // setEditPasswordLoading(true);
      return true;
    },
    onError: (data, variables, context) => {
      // setIsEditPasswordError(true);
      // setEditPasswordError(data as any);
      // setEditPasswordLoading(false);
      return true;
    },
    onSuccess: (data, variables, context) => {
      setWeightOpen(false);
      diaryDayRefetch();
      queryClient.invalidateQueries({ queryKey: ["diary_day", login?.id, currentDay] });
      // snack?
    },
  });
  const mutationDelete = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<{
        custom_result: {
          success: boolean,
          error?: string
        }
      }>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/remove`,
        pars.formData,
        login
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      return true;
    },
    onError: (data, variables, context) => {
      return true;
    },
    onSuccess: (data, variables, context) => {
      setNewEatMoment({feeling: -1, hunger: -1});
      setNewMoveMoment({feeling: -1, intensity: -1});
      setEatOpen(false);
      setMoveOpen(false);
      diaryDayRefetch();
    },
  });







  
  const [weightOpen, setWeightOpen] = useState<boolean>(false);
  const [newWeight, setNewWeight] = useState<number>(diaryDay?.weight || 0);
  
  const [moveOpen, setMoveOpen] = useState<boolean>(false);
  const [newMoveMoment, setNewMoveMoment] = useState<MoveMmnt>({feeling: -1, intensity: -1});
  
  const [eatOpen, setEatOpen] = useState<boolean>(false);
  const [newEatMoment, setNewEatMoment] = useState<EatMmnt>({feeling: -1, hunger: -1});
  
  const [eatDayOverviewOpen, setEatDayOverviewOpen] = useState<boolean>(false);

  useEffect(() => {
    setNewWeight(diaryDay?.weight || newWeight || 0);
    setCurrentNote(diaryDay?.client_note || '');
    setCurrentNoteHidden(!!diaryDay?.client_note_hidden);
    setHidePractices(diaryDay?.hide_practices || '');
  }, [diaryDay]);
  useEffect(() => {
    if (!diaryDay?.hide_practices) setHidePractices((myPractices || []).filter(fff => !fff.is_daynote).map(mmm => mmm.practice_id).join(","));

    let _activeAdh:number|undefined = undefined;
    (myPractices || []).forEach(cnsnt => {
      if (cnsnt.enabled && !!cnsnt.adh_profile_id) {
        _activeAdh = cnsnt.adh_profile_id;
      }
    });
    setActiveAdh(_activeAdh || login?.adh_profile_id || login?.calculated_adh_profile_id);
  }, [myPractices, login?.adh_profile_id, login?.calculated_adh_profile_id]);

  useEffect(() => {
    let to = setTimeout(() => {
      if (!!diaryDay?.id && (currentNote !== diaryDay?.client_note || currentNoteHidden !== diaryDay?.client_note_hidden || hidePractices !== diaryDay?.hide_practices)) {
        mutationSaveClientNote.mutate({formData: {
          object: 'diary_day',
          class_action: 'custom_api_save_client_note',
          id: diaryDay?.id,
          client_note: currentNote,
          client_note_hidden: currentNoteHidden,
          hide_practices: hidePractices
        }});
      }
    }, 2000);

    return () => {
      clearTimeout(to);
    }
  }, [currentNote, currentNoteHidden, hidePractices]);

  let inputWeightDisabled = false;
  if (!!diaryDaySuccess && !!diaryDay.weight) inputWeightDisabled = true;

  let inputWakeDisabled = false;
  if (!!diaryDaySuccess && !!diaryDay.wake_time) inputWakeDisabled = true;
  let inputSleepDisabled = false;
  if (!!diaryDaySuccess && !!diaryDay.sleep_time) inputSleepDisabled = true;

  const stepperStep = (time:string, completed: boolean, label:string) => {
    return <Step
      key={time}
      onClick={(e) => {
        e.preventDefault();
        setNewEatMoment({
          // ...newEatMoment,
          eat_type: time,
          ...(!!diaryDay ? diaryDay[time as 'breakfast' | 'lunch' | 'diner' | 'snack1' | 'snack2' | 'snack3'] : {})
        });
        setEatOpen(true);
        // navigate({pathname: "/moment", state: {time: 'breakfast', day: moment(this.state.day).format("DD/MM/YYYY"), moment: this.state.diary_day.attributes.breakfast}});
      }}
      sx={{
        '& > span': {
          cursor: 'pointer !important',
          '&:hover': {
            xs: {},
            md: {
              '& svg': {
                color: theme.palette.primary.main
              }
            }
          }
        },
        '&:hover > svg': {
          color: theme.palette.primary.main
        },
        position: 'relative'
      }}
      completed={completed}
      active={false}
    >
      <Edit sx={{
        scale: '0.9',
        position: 'absolute',
        top: -20,
        right: "30%",
        color: 'rgba(0, 0, 0, 0.38)',
        cursor: 'pointer'
      }} />
      <StepLabel>{label}</StepLabel>
    </Step>;
  }

  const GaugePointer = ({ pointerValue, color }: {pointerValue: number; color: string;}) => {
    const {
      startAngle,
      endAngle,
      innerRadius,
      outerRadius,
      cx,
      cy,
      valueMin,
      valueMax,
    } = useGaugeState();

    const angle =
      startAngle + ((endAngle - startAngle) * (pointerValue - valueMin)) / (valueMax - valueMin);

    const start = {
      x: cx + (innerRadius - 6) * Math.sin(angle),
      y: cy - (innerRadius - 6) * Math.cos(angle),
    };
    const end = {
      x: cx + (outerRadius + 6) * Math.sin(angle),
      y: cy - (outerRadius + 6) * Math.cos(angle),
    };

    return (
      <g>
        <path
          d={`M ${start.x} ${start.y} L ${end.x} ${end.y}`}
          stroke={color}
          strokeWidth={2}
        />
      </g>
    );
  }

  return <Page>
    <Grid container spacing={1}>
      <Grid item xs={12} md={6} sx={{
        textAlign: {
          xs: 'center',
          md: 'left'
        }
      }}>
        <IconButton
          onClick={(e) => {
            e.preventDefault();
            setCurrentDay(DateTime.fromFormat(currentDay, "dd/LL/y").minus({days: 1}).toFormat("dd/LL/y"));
            // this.setState({day: moment(this.state.day).add(-1, 'd')}, () => {
            //   this.fetch_day();
            // });
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <DatePicker
          // placeholder="DD/MM/YY"
          // autoOk={true}
          disableFuture={false}
          openTo="year"
          label=""
          format={"cccc dd/LL/y"}
          views={["year", "month", "day"]}
          setValue={(e) => {
            setCurrentDay(e);
            // this.setState({day: e}, () => {
            //   this.fetch_day();
            // });
          }}
          value={currentDay}
          id="option_date"
          sx={{
            box: {
              width: 'auto',
              display: 'inline-block'
            },
            field_box: {
              "svg": {
                display: 'none'
              }
            },
            field: {
              width: 'auto',
              marginTop: 0,
              "input": {
                textAlign: 'center'
              }
            }
          }}
        />
        <IconButton
          onClick={(e) => {
            e.preventDefault();
            setCurrentDay(DateTime.fromFormat(currentDay, "dd/LL/y").plus({days: 1}).toFormat("dd/LL/y"));
            // this.setState({day: moment(this.state.day).add(1, 'd')}, () => {
            //   this.fetch_day();
            // });
          }}
        >
          <ChevronRightIcon />
        </IconButton>

        {currentDay !== DateTime.now().toFormat("dd/LL/y") && <Button
          id="today"
          contained
          onClick={() => {
            setCurrentDay(DateTime.now().toFormat("dd/LL/y"));
          }}
          label={t("buttons.diary.today")}
          sx={{
            marginTop: 0,
            marginLeft: 1
          }}
        />}
      </Grid>
      <Grid item xs={12} md={6} sx={{
        textAlign: {
          xs: 'center',
          md: 'right'
        }
      }}>
        <Button
          fullwidth={false}
          onClick={(e) => {
            e.preventDefault();
            navigate("/diary_week", {state: {day: DateTime.fromFormat(currentDay, "dd/LL/y").startOf('week').toFormat("dd/LL/y")}});
          }}
          sx={{marginTop: 1}}
          id="goto_week_overview"
          label={t("client.diary.eat_diary.week_overview_action", "Weekoverzicht")}
          startIcon={<ViewWeekIcon />}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>

{/* DateTime.fromFormat(currentDay, "dd/LL/y").startOf("day") <= DateTime.now().startOf("day") &&  */}
      {(diaryDay?.external_feedbacks || []).filter(fff => !!fff.comment).length > 0 && <Grid item xs={12}>
        <Alert severity='info'>
          {(diaryDay?.external_feedbacks || []).map((ddd, ii) => <>
            <Typography sx={{fontWeight: 'bold', fontSize: '0.9rem'}}>{t("client.diary.coach_feedback", {coach: ddd.user_full_name, practice: ddd.practice_name})}</Typography>
            <Typography>{ddd.comment}</Typography>
            {ii < ((diaryDay?.external_feedbacks || []).length - 1) && <Divider sx={{marginTop: 1, marginBottom: 1}} />}
          </>)}
        </Alert>
      </Grid>}

      {DateTime.fromFormat(currentDay, "dd/LL/y").startOf("day") <= DateTime.now().startOf("day") && <Card
        loading={diaryDayLoading}
        actionNavFunc={() => {
          navigate("/wake", {state: {day: currentDay}});
        }}
        actionName={t("client.diary.woke_up_action")}
      >
        <Box sx={{textAlign: 'center'}}>
          <Typography variant="h5" sx={{marginBottom: 1}}>{t("client.diary.sleep_on", "Ik ben gaan slapen om")}</Typography>
          <Grid container spacing={3}>
            {inputSleepDisabled && !!diaryDay?.sleep_time && <Grid item xs={12} sx={{textAlign: 'center'}}>
              <Typography sx={{fontSize: '3rem'}}>{DateTime.fromISO(diaryDay.sleep_time).toFormat("t")}</Typography>
            </Grid>}
            {!inputSleepDisabled && <Grid item xs={12} sx={{textAlign: 'center'}}>
              <Button
                fullwidth={false}
                contained
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/wake", {state: {day: currentDay}});
                }}
                sx={{marginTop: 1}}
                id="enter_wake_time"
                label={t("client.diary.woke_up_cta", "Tijd ingeven")}
              />
            </Grid>}
          </Grid>

          <Typography variant="h5" sx={{marginTop: 3, marginBottom: 1}}>{t("client.diary.woke_up", "Ik ben wakker geworden om")}</Typography>
          <Grid container spacing={3}>
            {inputWakeDisabled && !!diaryDay?.wake_time && <Grid item xs={12} sx={{textAlign: 'center'}}>
              <Typography sx={{fontSize: '3rem'}}>{DateTime.fromISO(diaryDay.wake_time).toFormat("t")}</Typography>
            </Grid>}
            {!inputWakeDisabled && <Grid item xs={12} sx={{textAlign: 'center'}}>
              <Button
                fullwidth={false}
                contained
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/wake", {state: {day: currentDay}});
                }}
                sx={{marginTop: 1}}
                id="enter_wake_time"
                label={t("client.diary.woke_up_cta", "Tijd ingeven")}
              />
            </Grid>}
          </Grid>
        </Box>
      </Card>}

      {!!canDoCalculated && <Card
        title={t("client.dashboard.eat_circles.title")}
        actionNavFunc={() => {
          navigate("/calculation");
        }}
        actionName={t("client.dashboard.eat_circles.manage_profile", "Berekening aanpassen")}
        wide
      >
        <Box sx={{textAlign: 'center'}}>
          {!diaryDay?.latest_weight && <Alert severity='info' sx={{marginBottom: 1}}>
            <Typography>{t("client.dashboard.eat_circles.warning_no_weight")}</Typography>
          </Alert>}
          {typeof(login?.gender) === 'undefined' && <Alert severity='info' sx={{marginBottom: 1}}>
            <Typography>{t("client.dashboard.eat_circles.warning_no_gender")}</Typography>
          </Alert>}

          {(adhProfile?.adh_links || []).filter(xxx => !!xxx.is_tracked_client && !!xxx.nutritional_value?.is_client_enabled).sort((a, b) => (a.nutritional_value?.name || '').localeCompare(b.nutritional_value?.name || '')).map(adh_link => {
            let llimit = undefined;
            let hlimit = undefined;
            let mlimit = undefined;
            // absolute
            if (adh_link.measure_mode === 0) {
              llimit = adh_link.lower_limit || 0;
              hlimit = adh_link.higher_limit || 0;
              mlimit = adh_link.maximum || 0;
            }
            // gr/kg
            if (adh_link.measure_mode === 1 && !!diaryDay?.latest_weight) {
              if (!!adh_link.lower_limit) llimit = adh_link.lower_limit * diaryDay?.latest_weight;
              if (!!adh_link.higher_limit) hlimit = adh_link.higher_limit * diaryDay?.latest_weight;
              if (!!adh_link.maximum) mlimit = adh_link.maximum * diaryDay?.latest_weight;
            }
            // %EN
            if (adh_link.measure_mode === 2 && !!(adhProfile?.adh_links || []).filter(fff => fff.nutritional_value_id === 11)[0]) { // energie = nutritional value id 11
              let kcal_amount = (((diaryDay?.eat_calculation || {})[11]) || 0);
              if (!!adh_link.lower_limit) llimit = (adh_link.lower_limit * kcal_amount) / (100 * (adh_link.nutritional_value?.en_factor || 1));
              if (!!adh_link.higher_limit) hlimit = (adh_link.higher_limit * kcal_amount) / (100 * (adh_link.nutritional_value?.en_factor || 1));
              if (!!adh_link.maximum) mlimit = (adh_link.maximum * kcal_amount) / (100 * (adh_link.nutritional_value?.en_factor || 1));
            }
            // rest
            let real_val = (((diaryDay?.eat_calculation || {})[adh_link.nutritional_value_id || 0]) || 0);
            let act_val = parseFloat((real_val/(mlimit || hlimit || 1)*100).toFixed(2));
            let act_val_capped = act_val > 100 ? 100 : act_val;
            return <Box sx={{width: '140px', height: '110px', display: 'inline-block'}}>
              {(!!llimit || !!hlimit || !!mlimit) && <GaugeContainer
                width={140}
                height={140}
                startAngle={-110}
                endAngle={110}
                value={act_val_capped}
                sx={{
                  "& .MuiGauge-valueText": {
                    fontSize: 12,
                    transform: 'translate(0px, 0px)',
                  },
                  height: '100px',
                  opacity: real_val <= 0 ? 0.4 : 1
                }}
              >
                <GaugeReferenceArc />
                <GaugeValueArc style={{
                  ...(!!hlimit && real_val >= hlimit ? {
                    fill: 'yellow'
                  } : {}),
                  ...(!!mlimit && real_val >= mlimit ? {
                    fill: 'red'
                  } : {})
                }} />
                {/* <GaugeValueText text={(rf:any) => `${adh_link.nutritional_value?.name}\n${parseFloat(real_val.toString()).toFixed(2)}${adh_link.nutritional_value?.measure}${!!mlimit ? `\nmax:${mlimit}${adh_link.nutritional_value?.measure}` : ''}`} /> */}
                {!!llimit && <GaugePointer pointerValue={(llimit || 0)/(mlimit || hlimit || 1)*100} color="grey" />}
                {!!hlimit && <GaugePointer pointerValue={(hlimit || 0)/(mlimit || hlimit || 1)*100} color="blue" />}
                {!!mlimit && <GaugePointer pointerValue={(mlimit || 0)/(mlimit || hlimit || 1)*100} color="red" />}
              </GaugeContainer>}
              <Typography sx={{textDecoration: 'underline', fontSize: '12px', marginTop: (!!llimit || !!hlimit || !!mlimit) ? '-48px' : '0px', opacity: real_val <= 0 ? 0.4 : 1}}>{adh_link.nutritional_value?.name}</Typography>
              <Typography sx={{fontSize: '12px', opacity: real_val <= 0 ? 0.4 : 1}}>{parseFloat(real_val.toString()).toLocaleString(i18n.resolvedLanguage || 'nl', {maximumFractionDigits: 1})}{adh_link.nutritional_value?.measure}</Typography>
              <Typography sx={{fontSize: '9px', opacity: real_val <= 0 ? 0.4 : 1}}>{[
                (!llimit && !hlimit ? `&nbsp;` : undefined),
                (!!llimit && !hlimit ? `&gt;` : undefined),
                (!!llimit ? `${llimit.toLocaleString(i18n.resolvedLanguage || 'nl', {maximumFractionDigits: 1})}${adh_link.nutritional_value?.measure}` : undefined),
                (!!llimit && !!hlimit ? `-` : undefined),
                (!llimit && !!hlimit ? `&lt;` : undefined),
                (!!hlimit ? `${hlimit.toLocaleString(i18n.resolvedLanguage || 'nl', {maximumFractionDigits: 1})}${adh_link.nutritional_value?.measure}` : undefined)
              ].filter(fff => !!fff).join(" ")}</Typography>
              <Typography sx={{fontSize: '9px', opacity: real_val <= 0 ? 0.4 : 1}}>{!!mlimit ? `max: ${mlimit.toLocaleString(i18n.resolvedLanguage || 'nl', {maximumFractionDigits: 1})}${adh_link.nutritional_value?.measure}` : '&nbsp;'}</Typography>
            </Box>})}
        </Box>
      </Card>}

      <Card
        title={t("client.dashboard.move_diary.title", "Mijn beweegdagboek")}
        loading={diaryDayLoading}
        actionNavFunc={() => {
          setNewMoveMoment({});
          setMoveOpen(true);
        }}
        actionName={t("client.dashboard.measures.move_diary.cta", "Beweegmoment ingeven")}
      >
        <Box sx={{textAlign: 'center'}}>
          <Grid container spacing={3}>
            {!!diaryDay && (diaryDay.move_moment_list || []).length === 0 && <Grid item xs={12} sx={{textAlign: 'center'}}>
              <Typography variant="caption" sx={{marginTop: 1, fontStyle: 'italic'}}>{t("client.dashboard.move_diary.no_entries")}</Typography>
            </Grid>}
            {!!diaryDay && (diaryDay.move_moment_list || []).length > 0 && <Grid item xs={12} sx={{textAlign: 'center'}}>
              {(diaryDay?.move_moment_list || []).map(moveMoment => <MoveMoment mmoment={moveMoment} onClick={() => {
                setNewMoveMoment(moveMoment);
                setMoveOpen(true);
              }} />)}
            </Grid>}
          </Grid>
        </Box>
      </Card>

      <Card
        title={t("client.dashboard.eat_diary.title", "Mijn eetdagboek")}
        loading={diaryDayLoading}
        actionNavFunc={() => {
          setEatDayOverviewOpen(true);
        }}
        actionName={t("client.diary.eat_diary.overview_action")}
        huge
      >
        <Box sx={{
          textAlign: 'center',
          display: {
            xs: 'none',
            md: 'block'
          },
          marginTop: 3
        }}>
          <Stepper activeStep={-1} alternativeLabel nonLinear>
            {stepperStep("breakfast", !!diaryDay?.breakfast, t("client.diary.breakfast", "Ontbijt"))}
            {stepperStep("snack1", !!diaryDay?.snack1, t("client.diary.snack1", "Snack 1"))}
            {stepperStep("lunch", !!diaryDay?.lunch, t("client.diary.lunch", "Lunch"))}
            {stepperStep("snack2", !!diaryDay?.snack2, t("client.diary.snack2", "Snack 2"))}
            {stepperStep("diner", !!diaryDay?.diner, t("client.diary.diner", "Diner"))}
            {stepperStep("snack3", !!diaryDay?.snack3, t("client.diary.snack3", "Snack 3"))}
          </Stepper>
        </Box>
        <Box sx={{
          textAlign: 'center',
          display: {
            xs: 'block',
            md: 'none'
          },
          marginTop: 3
        }}>
          <Stepper activeStep={-1} alternativeLabel nonLinear>
            {stepperStep("breakfast", !!diaryDay?.breakfast, t("client.diary.breakfast", "Ontbijt"))}
            {stepperStep("snack1", !!diaryDay?.snack1, t("client.diary.snack1", "Snack 1"))}
            {stepperStep("lunch", !!diaryDay?.lunch, t("client.diary.lunch", "Lunch"))}
          </Stepper>
          <Stepper activeStep={-1} alternativeLabel nonLinear={true} sx={{marginTop: 3}}>
            {stepperStep("snack2", !!diaryDay?.snack2, t("client.diary.snack2", "Snack 2"))}
            {stepperStep("diner", !!diaryDay?.diner, t("client.diary.diner", "Diner"))}
            {stepperStep("snack3", !!diaryDay?.snack3, t("client.diary.snack3", "Snack 3"))}
          </Stepper>
        </Box>
      </Card>

      {diaryFeedbacksSuccess && diaryFeedbacks.length > 0 && <Card
        title={t("client.dashboard.my_feedback.title", "Mijn feedback")}
        loading={diaryFeedbacksLoading}
        huge
      >
        <List>
          {diaryFeedbacks.filter(ff => !!ff.thumb || !!ff.comment).map(feedback => <>
            <ListItem sx={{
              cursor: 'pointer'
            }} onClick={(e) => {
              navigate("/diary_week", {state: {day: DateTime.fromFormat(feedback.object_eat_moment_day_day || feedback.object_day_day, "y-LL-dd").startOf('week').toFormat("dd/LL/y")}});
            }}>
              <ListItemAvatar>
                <Avatar>
                  {feedback.object_type === "Diary::FoodEntry" && <BrunchDiningIcon />}
                  {feedback.object_type === "Diary::EatMoment" && <RestaurantIcon />}
                  {feedback.object_type === "Diary::MoveMoment" && <DirectionsRunIcon />}
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={<Typography>[{<Dt o={feedback.object_eat_moment_day_day || feedback.object_day_day} of="y-LL-dd" f="DDD" />}] {feedback.comment}{feedback.thumb && <ThumbUpAltIcon color="primary" sx={{marginBottom: "-5px"}} />}</Typography>} secondary={<Typography>{feedback.user_full_name} ~ {<Dt d={feedback.created_at} f="DDD t" />}</Typography>} />
            </ListItem>
          </>)}
        </List>
      </Card>}

      <Card
        title={t("client.dashboard.my_note.title")}
        loading={diaryFeedbacksLoading}
        wide={!!(DateTime.fromFormat(currentDay, "dd/LL/y").startOf("day") <= DateTime.now().startOf("day") && !login?.extra_data?.weight_trigger)}
        huge={!(DateTime.fromFormat(currentDay, "dd/LL/y").startOf("day") <= DateTime.now().startOf("day") && !login?.extra_data?.weight_trigger)}
      >
        <TextInput
          id="client_note"
          caption={t("client.models.diary_day.client_note")}
          noCaption
          multiline
          value={currentNote || ''}
          onChange={(e) => {
            setCurrentNote(e);
          }}
          sx={{
            marginTop: 2,
            marginBottom: 1
          }}
        />
        {/* <Switcher
          id="client_note_hidden"
          caption={t("client.models.diary_day.client_note_hidden")}
          value={currentNoteHidden}
          onChange={(s) => {
            setCurrentNoteHidden(!!s);
          }}
        /> */}
        <Grid item xs={12}>
          <Typography sx={{
            fontSize: '0.7rem'
          }}>{t("client.models.diary_day.visible_for_practices")}</Typography>
          {(myPractices || []).filter(fff => fff.enabled).map(consent => <Grid item xs={12}>
            <Checkbox checked={(hidePractices || '').split(",").indexOf(consent.practice_id?.toString() || '') < 0} onChange={(e) => {
              if (!!e.target.checked) {
                let oldIds = (hidePractices || '').split(",").filter(fff => !!fff);

                setHidePractices(`,${oldIds.filter(fff => fff !== (consent.practice_id?.toString() || '')).join(",")},`);
              }
              if (!e.target.checked) {
                let oldIds = (hidePractices || '').split(",").filter(fff => !!fff);

                setHidePractices(`,${[...oldIds, consent.practice_id].join(",")},`);
              }
            }} />
            <Typography html sx={{display: 'inline-block', cursor: 'pointer'}} onClick={(e) => {
              if ((hidePractices || '').split(",").indexOf(consent.practice_id?.toString() || '') > -1) {
                let oldIds = (hidePractices || '').split(",").filter(fff => !!fff);

                setHidePractices(`,${oldIds.filter(fff => fff !== (consent.practice_id?.toString() || '')).join(",")},`);
              }
              if ((hidePractices || '').split(",").indexOf(consent.practice_id?.toString() || '') < 0) {
                let oldIds = (hidePractices || '').split(",").filter(fff => !!fff);

                setHidePractices(`,${[...oldIds, consent.practice_id].join(",")},`);
              }
            }}>{consent.practice?.name}</Typography>
          </Grid>)}
        </Grid>
      </Card>

      {DateTime.fromFormat(currentDay, "dd/LL/y").startOf("day") <= DateTime.now().startOf("day") && !login?.extra_data?.weight_trigger && <Card
        title={t("client.dashboard.measures.weight.title", "Mijn gewicht")}
        loading={diaryDayLoading}
        actions={<>
          <Btn size="small" onClick={(e) => {
            e.preventDefault();
            navigate("/weight");
          }}>{t("client.dashboard.measures.weight.action")}</Btn>
          {inputWeightDisabled && <Btn size="small" onClick={(e) => {
            e.preventDefault();
            setWeightOpen(true);
          }}>{t("client.dashboard.measures.weight.action_edit", "Wijzig")}</Btn>}
        </>}
      >
        <Box sx={{textAlign: 'center'}}>
          <Grid container spacing={3}>
            {(login?.goal_weight || 0) > 0 && <Grid item xs={12} sx={{textAlign: 'center'}}>
              {t("client.measures.weight.goal_weight")}: {(login?.goal_weight || 0).toLocaleString(undefined, {maximumFractionDigits: 1})} kg
            </Grid>}
            {inputWeightDisabled && !!diaryDay?.weight && <Grid item xs={12} sx={{textAlign: 'center'}}>
              <Typography sx={{fontSize: '3rem'}}>{diaryDay?.weight.toLocaleString(undefined, {maximumFractionDigits: 1})}</Typography>
            </Grid>}
            {inputWeightDisabled && <Grid item xs={12} sx={{textAlign: 'center'}}>
              {t("client.measures.weight.motivational_placeholder")}
            </Grid>}
            {!inputWeightDisabled && <Grid item xs={12} sx={{textAlign: 'center'}}>
              <Button
                fullwidth={false}
                contained
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/weight", {state: {day: currentDay}});
                }}
                id="input_weight_btn"
                label={t("client.dashboard.measures.weight.cta", "Mijn gewicht nu ingeven")}
              />
            </Grid>}
          </Grid>
        </Box>
      </Card>}




      <ClientSaveWeight
        onClick={() => {
          mutationSaveWeight.mutate({
            formData: {
              object: "diary_day",
              class_action: 'custom_api_save_weight',
              id: diaryDay?.id,
              weight: newWeight,
              day: currentDay,
              user_id: login?.id,
              handler_id: login?.id
            }
          });
        }}
        open={weightOpen}
        setOpen={setWeightOpen}
        newWeight={newWeight}
        setNewWeight={setNewWeight}
        diaryDay={diaryDay}
      />
      <ClientEatMomentEdit
        canDoCalculated={canDoCalculated}
        onDelete={() => {
          if (!!newEatMoment?.id) mutationDelete.mutate({formData: {
            object: 'eat_moment',
            id: newEatMoment.id,
            ob: {
              id: newEatMoment.id,
            }
          }});
        }}
        onClick={() => {
          let formData = new FormData();
          formData.append('object', 'diary_day');
          formData.append('class_action', 'custom_api_save_eat_moment');
          formData.append('handler_id', (login?.id || "").toString());
          formData.append('id', (diaryDay?.id || "").toString());

          let ob = {
            ...newEatMoment,
            ...(!!newEatMoment.given_entry ? {
              entry_time: DateTime.now().set({hour: newEatMoment.given_entry.split(":")[0], minutes: newEatMoment.given_entry.split(":")[1]})
            } : {
              entry_time: DateTime.now(),
              given_entry: DateTime.now().toFormat("HH:mm")
            }),
            diary_day_id: diaryDay?.id,
            user_id: login?.id,
            entry_time_actual: DateTime.now()
          };
          Object.keys(ob).forEach(k => {
            // @ts-ignore
            if (k !== 'food_entries' && k !== 'item') formData.append(`ob[${k}]`, (ob[k]?.toString() || ''));
            if (k === 'item' && !!ob.item) formData.append(`ob[item]`, ob.item);
            if (k === 'food_entries') {
              if ((ob.food_entries || []).length < 1) formData.append(`ob[food_entries]`, 'empty');
              (ob.food_entries || []).forEach((fe, i) => {
                if (!!fe.id) formData.append(`ob[food_entries][${i}][id]`, (fe.id || '').toString());
                formData.append(`ob[food_entries][${i}][entry]`, fe.entry || '');
                formData.append(`ob[food_entries][${i}][comment]`, fe.comment || '');
                formData.append(`ob[food_entries][${i}][foodstuff_id]`, fe.foodstuff_id?.toString() || '');
                formData.append(`ob[food_entries][${i}][foodstuff_measure_id]`, fe.foodstuff_measure_id?.toString() || '');
                formData.append(`ob[food_entries][${i}][foodstuff_portion]`, fe.foodstuff_portion?.toString() || '');
                if (!!fe.item) formData.append(`ob[food_entries][${i}][item]`, fe.item);
              });
            }
          });

          mutationSaveEatMoment.mutate({
            formData: formData
          });
        }}
        open={eatOpen}
        setOpen={setEatOpen}
        newEatMoment={newEatMoment}
        setNewEatMoment={setNewEatMoment}
        diaryDay={diaryDay}
      />
      <ClientMoveMomentEdit
        onDelete={() => {
          if (!!newMoveMoment?.id) mutationDelete.mutate({formData: {
            object: 'move_moment',
            id: newMoveMoment.id,
            ob: {
              id: newMoveMoment.id,
            }
          }});
        }}
        onClick={() => {
          mutationSaveMoveMoment.mutate({
            formData: {
              object: "move_moment",
              id: newMoveMoment?.id,
              ob: {
                ...newMoveMoment,
                ...(!!newMoveMoment.given_entry ? {
                  entry_time: DateTime.now().set({hour: newMoveMoment.given_entry.split(":")[0], minutes: newMoveMoment.given_entry.split(":")[1]})
                } : {
                  entry_time: DateTime.now(),
                  given_entry: DateTime.now().toFormat("HH:mm")
                }),
                diary_day_id: diaryDay?.id,
                user_id: login?.id,
                entry_time_actual: DateTime.now()
              },
              handler_id: login?.id
            }
          });
        }}
        open={moveOpen}
        setOpen={setMoveOpen}
        newMoveMoment={newMoveMoment}
        setNewMoveMoment={setNewMoveMoment}
        diaryDay={diaryDay}
      />
      {!!diaryDay && !!login && <EatDayOverview
        open={eatDayOverviewOpen}
        setOpen={setEatDayOverviewOpen}
        diaryDay={diaryDay}
        refetch={() => {
          diaryDayRefetch();
        }}
        user={login}
      />}
    
      <SnackSaved open={currentNoteSaved} setOpen={setCurrentNoteSaved} />


    </Grid>
  </Page>;
}

